// client/src/components/secure/ZoningLibraryAdmin.js — editing the Zoning Library.
//
//   LibraryEntryForm  add / edit a town's answer for a use, with an "AI draft" helper whose
//                     output is a DRAFT: it is saved as verified only when a person ticks
//                     the approval box. Change history underneath. Delete: managing broker.
//   ZoningQueue       addresses waiting for (or answered by) the office AI zoning check.
//   MapLayers         which online map answers for which town — adding a town is a row.
import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { getCurrentUser } from '../../hooks/useCurrentUser';
import DataList from '../shared/DataList';
import { BusinessPicker } from './BusinessPicker';

const field = 'mt-1 w-full rounded border border-gray-300 px-2 py-1.5 text-sm';
const label = 'block text-xs font-semibold uppercase tracking-wide text-gray-600';

const EMPTY = {
    municipality: '', county: '', use_category: '', verdict_status: 'unverified', verdict: '',
    districts_by_right: '', districts_special_use: '', conditions: '', citation: '', interpretation: '',
    source_url: '', verified_on: '', verified_by: '', official_name: '', official_title: '',
    official_phone: '', official_email: '', department_phone: '', notes: '',
};

const History = ({ jid }) => {
    const [rows, setRows] = useState(null);
    const apiClient = useApiClient();
    useEffect(() => {
        apiClient.get(`/api/site-searches/zoning-library/${jid}/history`).then(setRows).catch(() => setRows([]));
    }, [apiClient, jid]);
    if (!rows) return null;
    if (!rows.length) return <p className="text-xs text-gray-500">No changes recorded yet.</p>;
    return (
        <ul className="space-y-1 text-xs text-gray-600">
            {rows.map((r) => {
                const a = typeof r.after_json === 'string' ? JSON.parse(r.after_json || 'null') : r.after_json;
                const b = typeof r.before_json === 'string' ? JSON.parse(r.before_json || 'null') : r.before_json;
                const changed = a && b ? Object.keys(a).filter((k) => !['updated_at', 'created_at'].includes(k)
                    && JSON.stringify(a[k]) !== JSON.stringify(b[k])) : [];
                return (
                    <li key={r.change_id}>
                        <span className="font-mono text-[11px]">{new Date(r.created_at).toLocaleString()}</span> · <strong>{r.action.replace('_', ' ')}</strong>
                        {' '}· {r.changed_by}{changed.length ? ` · ${changed.join(', ')}` : ''}
                    </li>
                );
            })}
        </ul>
    );
};

export const LibraryEntryForm = ({ entry, onDone, onCancel }) => {
    const [f, setF] = useState(() => {
        const base = { ...EMPTY };
        for (const k of Object.keys(EMPTY)) if (entry && entry[k] != null) base[k] = String(entry[k]);
        if (base.verified_on) base.verified_on = base.verified_on.slice(0, 10);
        return base;
    });
    const [fromAi, setFromAi] = useState(entry?.interpretation_origin === 'ai_draft');
    const [approved, setApproved] = useState(false);
    const [ai, setAi] = useState({ open: false, text: '', url: '', busy: false, status: null, error: '' });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const apiClient = useApiClient();
    const user = getCurrentUser();
    const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

    const openAi = async () => {
        setAi({ ...ai, open: true });
        try { const s = await apiClient.get('/api/site-searches/zoning-library/ai-status'); setAi((x) => ({ ...x, status: s })); }
        catch (e) { setAi((x) => ({ ...x, status: { available: false, message: e.message } })); }
    };
    const runAi = async () => {
        setAi((x) => ({ ...x, busy: true, error: '' }));
        try {
            const r = await apiClient.post('/api/site-searches/zoning-library/ai-draft', {
                jurisdiction_id: entry?.jurisdiction_id || null, municipality: f.municipality, use_category: f.use_category,
                text: ai.text, url: ai.url,
            });
            const d = r.draft;
            setF({ ...f, verdict_status: d.verdict_status, verdict: d.verdict || f.verdict,
                districts_by_right: d.districts_by_right, districts_special_use: d.districts_special_use,
                citation: d.citation, conditions: d.conditions, interpretation: d.interpretation,
                source_url: ai.url || f.source_url, verified_on: '', verified_by: '' });
            setFromAi(true); setApproved(false);
            setAi((x) => ({ ...x, busy: false, open: false }));
        } catch (e) { setAi((x) => ({ ...x, busy: false, error: e.message })); }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true); setError('');
        const body = { ...f, from_ai: fromAi, human_approved: fromAi ? approved : undefined };
        if (fromAi) { delete body.verified_on; delete body.verified_by; }
        try {
            if (entry?.jurisdiction_id) await apiClient.put(`/api/site-searches/zoning-library/${entry.jurisdiction_id}`, body);
            else await apiClient.post('/api/site-searches/zoning-library', body);
            onDone();
        } catch (ex) { setError(ex.message); } finally { setSaving(false); }
    };
    const canManage = !!(user && (user.isManagingBroker || user.staffRole === 'Office Manager' || user.role === 'admin') && user.staffRole !== 'Read Only');
    const confirm = async () => {
        try { await apiClient.post(`/api/zoning-library/entries/${entry.jurisdiction_id}/confirm`, {}); onDone(); }
        catch (ex) { setError(ex.message); }
    };
    const remove = async () => {
        if (!window.confirm(`Delete the ${f.municipality} entry for "${f.use_category}"? It stays in the change history.`)) return;
        try { await apiClient.delete(`/api/site-searches/zoning-library/${entry.jurisdiction_id}`); onDone(); }
        catch (ex) { alert(ex.message); }
    };

    return (
        <form onSubmit={save} className="space-y-3">
            {error && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Town</label><input required className={field} value={f.municipality} onChange={set('municipality')} placeholder="Vernon Hills" /></div>
                <div><label className={label}>County</label><input className={field} value={f.county} onChange={set('county')} placeholder="Lake" /></div>
                <div><label className={label}>Business (use category)</label>
                    <BusinessPicker value={f.use_category} onChange={(v) => setF({ ...f, use_category: v })} required /></div>
            </div>

            {entry?.jurisdiction_id && (entry.interpretation_origin === 'ai_draft' || entry.code_changed_at) && (
                <div className="flex flex-wrap items-center gap-2 rounded border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <span><strong>{entry.interpretation_origin === 'ai_draft' ? 'AI draft — confirm.' : 'Town code changed — re-check.'}</strong>{' '}
                        {entry.code_changed_note || 'Read it against the code (the quoted rows and the citation below), correct anything wrong and save, or confirm it as it stands.'}</span>
                    {canManage && <button type="button" onClick={confirm} className="ml-auto rounded bg-amber-600 px-3 py-1 font-semibold text-white">Confirm as it stands</button>}
                </div>
            )}
            {entry?.interpretation_origin === 'derived' && (
                <p className="rounded border-l-4 border-indigo-400 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">Worked out from the town's accepted use table ({entry.citation}). Editing it makes it a person's entry; the use table's answer is then kept beside it.</p>
            )}
            {entry?.derived_status && entry.interpretation_origin !== 'derived' && entry.derived_status !== entry.verdict_status && (
                <p className="rounded border-l-4 border-red-400 bg-red-50 px-3 py-2 text-xs text-red-900">
                    The town's use table says <strong>{entry.derived_status.replace('_', ' ')}</strong>
                    {entry.derived_by_right ? ` (by right in ${entry.derived_by_right})` : ''}{entry.derived_special_use ? ` (special use in ${entry.derived_special_use})` : ''} — this entry says {entry.verdict_status.replace('_', ' ')}. Check which is right.</p>
            )}
            {Array.isArray(entry?.rules) && entry.rules.length > 0 && (
                <details className="rounded border border-gray-200 p-2 text-xs"><summary className="cursor-pointer font-semibold text-gray-600">Quoted code ({entry.rules.length})</summary>
                    <ul className="mt-1 space-y-1">{entry.rules.map((r, i) => <li key={i} className="font-mono text-[11px] text-gray-700">“{r}”</li>)}</ul></details>
            )}

            <div className="rounded border border-indigo-100 bg-indigo-50/40 p-2 text-xs">
                {!ai.open && <button type="button" onClick={openAi} className="rounded bg-slate-800 px-3 py-1 font-semibold text-white">AI draft</button>}
                {!ai.open && <span className="ml-2 text-gray-600">Paste the code section (or give its address) and let the AI draft the reading. You check and approve it.</span>}
                {ai.open && ai.status && !ai.status.available && (
                    <p className="text-amber-800">{ai.status.message}</p>
                )}
                {ai.open && ai.status?.available && (
                    <div className="space-y-2">
                        <textarea rows={5} value={ai.text} onChange={(e) => setAi({ ...ai, text: e.target.value })}
                            placeholder="Paste the code text: the use table, the district sections, the definition…" className={field} />
                        <input value={ai.url} onChange={(e) => setAi({ ...ai, url: e.target.value })}
                            placeholder="…or the https:// address of the code section (web page, not PDF)" className={field} />
                        <div className="flex gap-2">
                            <button type="button" disabled={ai.busy} onClick={runAi} className="rounded bg-slate-800 px-3 py-1 font-semibold text-white disabled:opacity-50">
                                {ai.busy ? 'Drafting…' : 'Draft it'}</button>
                            <button type="button" onClick={() => setAi({ ...ai, open: false })} className="rounded border border-gray-300 px-3 py-1">Close</button>
                        </div>
                        {ai.error && <p className="text-red-700">{ai.error}</p>}
                    </div>
                )}
                {ai.open && !ai.status && <p className="text-gray-500">Checking…</p>}
            </div>

            {fromAi && (
                <p className="rounded border-l-4 border-amber-500 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                    <strong>AI DRAFT.</strong> Read it against the code and correct anything wrong. It is saved as unverified unless you tick the approval box below.
                </p>
            )}

            <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Answer</label>
                    <select className={field} value={f.verdict_status} onChange={set('verdict_status')}>
                        <option value="by_right">By right</option><option value="special_use">Special use</option>
                        <option value="not_allowed">Not allowed</option><option value="unverified">Unverified</option>
                    </select></div>
                <div><label className={label}>By right in districts</label><input className={field} value={f.districts_by_right} onChange={set('districts_by_right')} placeholder="I, O" /></div>
                <div><label className={label}>Special use in districts</label><input className={field} value={f.districts_special_use} onChange={set('districts_special_use')} placeholder="B-2, B-3" /></div>
            </div>
            <div><label className={label}>One-line answer</label><input required className={field} value={f.verdict} onChange={set('verdict')} placeholder="BY RIGHT in I and O — capped at 100 students" /></div>
            <div className="grid grid-cols-2 gap-3">
                <div><label className={label}>Citation / code section</label><input className={field} value={f.citation} onChange={set('citation')} placeholder="Title 6 Ch. 8, use table" /></div>
                <div><label className={label}>Code source address</label><input className={field} value={f.source_url} onChange={set('source_url')} placeholder="https://…" /></div>
            </div>
            <div><label className={label}>Conditions</label><input className={field} value={f.conditions} onChange={set('conditions')} placeholder="100 students on site; 1 per 25 SF" /></div>
            <div><label className={label}>Interpretation</label><textarea rows={4} className={field} value={f.interpretation} onChange={set('interpretation')} /></div>

            {fromAi ? (
                <label className="flex items-start gap-2 rounded border border-gray-200 p-2 text-sm">
                    <input type="checkbox" checked={approved} onChange={(e) => setApproved(e.target.checked)} className="mt-1" />
                    <span>I have read the code and approve this interpretation as edited. (Records it as verified today, by me.)</span>
                </label>
            ) : (
                <div className="grid grid-cols-2 gap-3">
                    <div><label className={label}>Verified on</label><input type="date" className={field} value={f.verified_on} onChange={set('verified_on')} /></div>
                    <div><label className={label}>Verified by</label><input className={field} value={f.verified_by} onChange={set('verified_by')} placeholder="(you, if left blank)" /></div>
                </div>
            )}

            <details className="rounded border border-gray-200 p-2">
                <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">Village contact and notes</summary>
                <div className="mt-2 grid grid-cols-2 gap-3">
                    <div><label className={label}>Official</label><input className={field} value={f.official_name} onChange={set('official_name')} /></div>
                    <div><label className={label}>Title</label><input className={field} value={f.official_title} onChange={set('official_title')} /></div>
                    <div><label className={label}>Phone</label><input className={field} value={f.official_phone} onChange={set('official_phone')} /></div>
                    <div><label className={label}>Email</label><input className={field} value={f.official_email} onChange={set('official_email')} /></div>
                    <div><label className={label}>Department phone</label><input className={field} value={f.department_phone} onChange={set('department_phone')} /></div>
                    <div className="col-span-2"><label className={label}>Notes</label><textarea rows={2} className={field} value={f.notes} onChange={set('notes')} /></div>
                </div>
            </details>

            {entry?.jurisdiction_id && (
                <details className="rounded border border-gray-200 p-2">
                    <summary className="cursor-pointer text-xs font-semibold uppercase tracking-wide text-gray-500">Change history</summary>
                    <div className="mt-2"><History jid={entry.jurisdiction_id} /></div>
                </details>
            )}

            <div className="flex items-center justify-between pt-1">
                <div>{entry?.jurisdiction_id && user?.isManagingBroker && (
                    <button type="button" onClick={remove} className="rounded border border-red-300 px-3 py-2 text-sm text-red-700">Delete entry</button>)}</div>
                <div className="flex gap-2">
                    <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                    <button type="submit" disabled={saving} className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">
                        {saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </form>
    );
};

const CHECK_TONE = {
    queued: 'bg-indigo-50 text-indigo-700', answered: 'bg-green-50 text-green-700',
    failed: 'bg-red-50 text-red-700', cancelled: 'bg-gray-100 text-gray-500',
};

const isUnconfirmed = (r) => !!(r.lookup_district && r.district_source === 'ai_read' && !Number(r.district_confirmed));

export const ZoningQueue = () => {
    const [rows, setRows] = useState(null);
    const [show, setShow] = useState('');
    const apiClient = useApiClient();
    const load = useCallback(async () => {
        try { setRows(await apiClient.get(`/api/site-searches/zoning-checks${show ? `?status=${show}` : ''}`)); }
        catch { setRows([]); }
    }, [apiClient, show]);
    useEffect(() => { load(); }, [load]);
    const act = async (path, body) => {
        try { const r = await apiClient.post(path, body || {}); if (r?.message) alert(r.message); load(); }
        catch (e) { alert(e.message); }
    };
    if (!rows) return null;
    return (
        <div className="space-y-2">
            <div className="flex items-center gap-2 text-xs">
                <span className="text-gray-600">Addresses in towns with no online zoning map are read by the office AI (about hourly, while the office computer is on).</span>
                <select value={show} onChange={(e) => setShow(e.target.value)} className="ml-auto rounded border border-gray-300 px-2 py-1">
                    <option value="">All</option><option value="queued">Queued</option><option value="answered">Answered</option><option value="failed">Could not read</option>
                </select>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
                <DataList key={show} rows={rows} rowKey="request_id" csvName="zoning-checks" defaultSort={{ key: 'created_at', dir: 'desc' }}
                    empty="Nothing in the queue." filters={[{ key: 'municipality', label: 'Town' }, { key: 'use_category', label: 'Use' }]}
                    columns={[
                        { key: 'address', label: 'Address', value: (r) => r.matched_address || r.address, render: (r) => (<>
                            <p className="font-semibold">{r.matched_address || r.address}</p>
                            <p className="text-xs text-gray-500">{r.municipality}{r.pin ? ` · PIN ${r.pin}` : ''} · asked {new Date(r.created_at).toLocaleDateString()}{r.requested_by_name ? ` by ${r.requested_by_name}` : ''}</p>
                        </>) },
                        { key: 'municipality', label: 'Town' },
                        { key: 'created_at', label: 'Asked', type: 'date' },
                        { key: 'use_category', label: 'Use', render: (r) => <span className="text-xs">{r.use_category || '—'}</span> },
                        { key: 'status', label: 'Status', value: (r) => (r.status === 'failed' ? 'could not read' : r.status), render: (r) => (<>
                            <span className={`rounded px-2 py-0.5 text-xs ${CHECK_TONE[r.status]}`}>{r.status === 'failed' ? 'could not read' : r.status}</span>
                            {r.status === 'queued' && r.attempts > 0 && <span className="block text-[11px] text-gray-500">{r.attempts} tr{r.attempts === 1 ? 'y' : 'ies'}</span>}
                        </>) },
                        { key: 'district', label: 'District', value: (r) => r.lookup_district || r.district || '', render: (r) => {
                            const unconfirmed = isUnconfirmed(r);
                            return (<>
                                <span className="font-mono text-xs">{r.lookup_district || r.district || '—'}</span>
                                {r.lookup_district && <span className={`block text-[11px] ${unconfirmed ? 'font-semibold text-amber-700' : 'text-gray-500'}`}>
                                    {r.district_source === 'ai_read' ? (unconfirmed ? `AI-read from ${r.source || 'the town map'} — confirm` : `confirmed by ${r.confirmed_by}`) : r.district_source === 'manual' ? 'typed by staff' : 'online map'}</span>}
                            </>);
                        } },
                        { key: 'notes', label: 'Notes', value: (r) => `${r.notes || ''}${r.confidence ? ` (confidence: ${r.confidence})` : ''}`, render: (r) => (
                            <span className="text-xs text-gray-600">{r.notes}{r.confidence ? ` (confidence: ${r.confidence})` : ''}
                                {r.map_link && <a href={r.map_link} target="_blank" rel="noreferrer" className="block text-blue-700 hover:underline">Town map ↗</a>}</span>) },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (r) => (
                            <span className="whitespace-nowrap text-xs">
                                {isUnconfirmed(r) && <button onClick={() => act(`/api/site-searches/zoning-lookup/${r.lookup_id}/confirm`, { use_category: r.use_category })} className="mr-1 rounded bg-amber-600 px-2 py-1 font-semibold text-white">Confirm</button>}
                                {r.status === 'queued' && <button onClick={() => act(`/api/site-searches/zoning-checks/${r.request_id}/cancel`)} className="rounded border border-gray-300 px-2 py-1">Cancel</button>}
                                {(r.status === 'failed' || r.status === 'cancelled') && <button onClick={() => act(`/api/site-searches/zoning-checks/${r.request_id}/requeue`)} className="rounded border border-gray-300 px-2 py-1">Ask again</button>}
                            </span>) },
                    ]} />
            </div>
            <p className="text-[11px] text-gray-500">To type a district yourself, look the address up above and use "Type the district myself".</p>
        </div>
    );
};

const LAYER_EMPTY = { kind: 'zoning', municipality: '', county: '', service_type: 'arcgis', service_url: '', district_field: '',
    pin_field: '', municipality_field: '', exclude_districts: '', ignore_values: '', map_link: '', notes: '', active: 1 };

const LayerForm = ({ layer, onDone, onCancel }) => {
    const [f, setF] = useState(() => ({ ...LAYER_EMPTY, ...Object.fromEntries(Object.entries(layer || {}).map(([k, v]) => [k, v == null ? '' : v])) }));
    const [err, setErr] = useState('');
    const apiClient = useApiClient();
    const set = (k) => (e) => setF({ ...f, [k]: e.target.type === 'checkbox' ? (e.target.checked ? 1 : 0) : e.target.value });
    const save = async (e) => {
        e.preventDefault(); setErr('');
        try {
            if (layer?.layer_id) await apiClient.put(`/api/site-searches/zoning-layers/${layer.layer_id}`, f);
            else await apiClient.post('/api/site-searches/zoning-layers', f);
            onDone();
        } catch (ex) { setErr(ex.message); }
    };
    return (
        <form onSubmit={save} className="space-y-3">
            {err && <p className="rounded bg-red-50 px-3 py-2 text-sm text-red-700">{err}</p>}
            <div className="grid grid-cols-3 gap-3">
                <div><label className={label}>Kind</label>
                    <select className={field} value={f.kind} onChange={set('kind')}>
                        <option value="zoning">Town zoning map (online)</option>
                        <option value="no_map">Town with no online map</option>
                        <option value="parcels">County parcels (PIN)</option>
                    </select></div>
                {f.kind === 'parcels'
                    ? <div><label className={label}>County</label><input required className={field} value={f.county} onChange={set('county')} placeholder="Lake" /></div>
                    : <div><label className={label}>Town</label><input required className={field} value={f.municipality} onChange={set('municipality')} placeholder="Skokie" /></div>}
                <label className="mt-6 flex items-center gap-2 text-sm"><input type="checkbox" checked={!!Number(f.active)} onChange={set('active')} />In use</label>
            </div>
            {f.kind !== 'no_map' && (
                <>
                    <div className="grid grid-cols-4 gap-3">
                        <div><label className={label}>Service</label>
                            <select className={field} value={f.service_type} onChange={set('service_type')}>
                                <option value="arcgis">ArcGIS</option><option value="socrata">Socrata</option></select></div>
                        <div className="col-span-3"><label className={label}>Query address</label><input required className={field} value={f.service_url} onChange={set('service_url')} placeholder="https://…/MapServer/66/query" /></div>
                    </div>
                    {f.kind === 'zoning' ? (
                        <div className="grid grid-cols-3 gap-3">
                            <div><label className={label}>District field</label><input required className={field} value={f.district_field} onChange={set('district_field')} placeholder="ZONED" /></div>
                            <div><label className={label}>Excluded districts</label><input className={field} value={f.exclude_districts} onChange={set('exclude_districts')} placeholder="PMD" /></div>
                            <div><label className={label}>Values to ignore</label><input className={field} value={f.ignore_values} onChange={set('ignore_values')} placeholder="Zone" /></div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className={label}>PIN field</label><input className={field} value={f.pin_field} onChange={set('pin_field')} placeholder="PIN" /></div>
                            <div><label className={label}>Town field</label><input className={field} value={f.municipality_field} onChange={set('municipality_field')} placeholder="MUNI_NAME" /></div>
                        </div>
                    )}
                </>
            )}
            <div><label className={label}>Map link for people</label><input className={field} value={f.map_link} onChange={set('map_link')} placeholder="https://… (the PDF map or the village zoning page)" /></div>
            <div><label className={label}>Notes</label><input className={field} value={f.notes} onChange={set('notes')} /></div>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="rounded border border-gray-300 px-4 py-2 text-sm">Cancel</button>
                <button className="rounded bg-slate-800 px-4 py-2 text-sm font-semibold text-white">Save</button>
            </div>
        </form>
    );
};

export const MapLayers = ({ Modal }) => {
    const [rows, setRows] = useState(null);
    const [editing, setEditing] = useState(null);
    const apiClient = useApiClient();
    const user = getCurrentUser();
    const load = useCallback(async () => {
        try { setRows(await apiClient.get('/api/site-searches/zoning-layers')); } catch { setRows([]); }
    }, [apiClient]);
    useEffect(() => { load(); }, [load]);
    const remove = async (l) => {
        if (!window.confirm(`Delete the map row for ${l.municipality || l.county}?`)) return;
        try { await apiClient.delete(`/api/site-searches/zoning-layers/${l.layer_id}`); load(); } catch (e) { alert(e.message); }
    };
    if (!rows) return null;
    const KIND = { zoning: 'Online zoning map', no_map: 'No online map', parcels: 'County parcels' };
    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between text-xs text-gray-600">
                <span>Which online map the address lookup asks, per town. A town not listed is treated as having no online map.</span>
                <button onClick={() => setEditing({})} className="rounded bg-gray-800 px-3 py-1 font-semibold text-white">Add a town or county</button>
            </div>
            <div className="rounded border border-gray-200 bg-white p-3">
                <DataList rows={rows} rowKey="layer_id" csvName="zoning-map-layers" defaultSort={{ key: 'place', dir: 'asc' }}
                    empty="No towns or counties listed yet." filters={[{ key: 'kind', label: 'Kind' }, { key: 'in_use', label: 'In use' }]}
                    rowClass={(l) => (Number(l.active) ? '' : 'text-gray-400')}
                    columns={[
                        { key: 'place', label: 'Town / county', value: (l) => l.municipality || `${l.county} County`, render: (l) => <span className="font-semibold">{l.municipality || `${l.county} County`}</span> },
                        { key: 'kind', label: 'Kind', value: (l) => KIND[l.kind] || l.kind, render: (l) => <span className="text-xs">{KIND[l.kind]}{Number(l.active) ? '' : ' (off)'}</span> },
                        { key: 'in_use', label: 'In use', value: (l) => (Number(l.active) ? 'yes' : 'no') },
                        { key: 'field', label: 'Field', value: (l) => l.district_field || l.pin_field || '', render: (l) => <span className="font-mono text-[11px]">{l.district_field || l.pin_field || '—'}</span> },
                        { key: 'notes', label: 'Notes', render: (l) => (
                            <span className="text-xs text-gray-600">{l.notes}
                                {l.map_link && <a href={l.map_link} target="_blank" rel="noreferrer" className="ml-1 text-blue-700 hover:underline">map ↗</a>}</span>) },
                        { key: 'actions', label: '', sortable: false, csv: false, search: false, render: (l) => (
                            <span className="whitespace-nowrap text-xs">
                                <button onClick={() => setEditing(l)} className="rounded border border-gray-300 px-2 py-1">Edit</button>
                                {user?.isManagingBroker && <button onClick={() => remove(l)} className="ml-1 rounded border border-red-300 px-2 py-1 text-red-700">Delete</button>}
                            </span>) },
                    ]} />
            </div>
            <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.layer_id ? 'Edit map row' : 'Add a town or county'}>
                {editing && <LayerForm layer={editing.layer_id ? editing : null} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}
            </Modal>
        </div>
    );
};
